import { useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { useLocalizedErrorMessage } from '../../hooks/useLocalizedErrorMessage';
import { en } from '../../locales/en';
import VehicleStatusTile from '../../pages/dashboard/side-panel/VehicleStatusTile';
import { selectVehicleStatusCategory } from '../../store/selectors/selectVehicleStatusCategory';
import { useGcsStore } from '../../store/useGcsStore';
import { AppError, ErrorCode, ErrorDomain } from '../../types/errors/errors';
import type { MissionId } from '../../types/gcs/mission';
import { AsyncAction } from '../../types/general/asyncAction';
import { TrainingControlsCommand } from '../../types/ui/trainingControlsAction';
import { getShortVehicleId } from '../../utils/formatters';
import { deserializeGcsVehicleId, serializeGcsVehicleId } from '../../utils/gcsVehicleId';
import { SecondaryButton } from '../buttons/SecondaryButton';
import DropDownSearchList from '../general/DropDownSearchList';
import { Text } from '../general/Text';
import Dialog from './Dialog';

interface TrainingControlsDialogProps {
  onClose: () => void;
}

function getRequiredMissionId(missionId: MissionId | null): MissionId {
  if (!missionId) {
    throw new AppError({
      domain: ErrorDomain.BUSINESS,
      code: ErrorCode.MISSION_NOT_FOUND
    });
  }

  return missionId;
}

function TrainingControlsDialog({ onClose }: TrainingControlsDialogProps) {
  const [selectedVehicleJointId, setSelectedVehicleJointId] = useState<string | null>(null);
  const [selectedCommand, setSelectedCommand] = useState<TrainingControlsCommand | null>(null);
  const [action, setAction] = useState<AsyncAction<void, unknown>>(AsyncAction.initial());
  const getLocalizedErrorMessage = useLocalizedErrorMessage();

  const gcsVehicleIds = useGcsStore(
    useShallow((state) => {
      const ids: string[] = [];

      state.data.forEach((gcsData, gcsId) => {
        if (!gcsData.center.data?.isTrainingModeEnabled) return;

        const vehicles = gcsData.vehicles.data;
        if (!vehicles) return;

        vehicles.forEach((vehicle) => {
          const vehicleCategory = selectVehicleStatusCategory(state.data, gcsId, vehicle.id);
          if (!vehicleCategory) return;
          ids.push(serializeGcsVehicleId(gcsId, vehicle.id));
        });
      });

      return ids.sort((a, b) => deserializeGcsVehicleId(a).vehicleId - deserializeGcsVehicleId(b).vehicleId);
    })
  );

  const selectedMissionId = useGcsStore((state): MissionId | null => {
    if (!selectedVehicleJointId) return null;
    const { gcsId, vehicleId } = deserializeGcsVehicleId(selectedVehicleJointId);
    const gcsData = state.data.get(gcsId);
    return gcsData?.liveMissionsMap.get(vehicleId)?.missionId ?? null;
  });

  const matchesVehicleQuery = (jointId: string, query: string) => {
    const normalizedQuery = query.toLowerCase();
    return deserializeGcsVehicleId(jointId).vehicleId.toString().includes(normalizedQuery);
  };

  const renderVehicleResultRow = (jointId: string) => {
    const { gcsId, vehicleId } = deserializeGcsVehicleId(jointId);

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '8px',
          width: '100%'
        }}
      >
        <Text
          as='span'
          variant='body2-medium'
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {getShortVehicleId(vehicleId)}
        </Text>
        <VehicleStatusTile gcsId={gcsId} vehicleId={vehicleId} />
      </div>
    );
  };

  const executeAction = async (command: TrainingControlsCommand) => {
    if (!selectedVehicleJointId || action.isLoading) return;
    const { gcsId, vehicleId } = deserializeGcsVehicleId(selectedVehicleJointId);
    const store = useGcsStore.getState();

    setSelectedCommand(command);
    setAction(AsyncAction.loading());

    try {
      switch (command) {
        case TrainingControlsCommand.RTL:
          await store.rtl(gcsId, getRequiredMissionId(selectedMissionId));
          break;
        case TrainingControlsCommand.FC_LOSS:
          await store.triggerFcLoss(gcsId, getRequiredMissionId(selectedMissionId));
          break;
        case TrainingControlsCommand.SAFE_UP:
          await store.safeUp(gcsId, getRequiredMissionId(selectedMissionId));
          break;
        case TrainingControlsCommand.VEHICLE_DISCONNECTION:
          await store.disconnectVehicle(gcsId, vehicleId);
          break;
        case TrainingControlsCommand.LAND:
          await store.emergencyLand(gcsId, getRequiredMissionId(selectedMissionId));
          break;
        case TrainingControlsCommand.FTS:
          await store.triggerParachute(gcsId, getRequiredMissionId(selectedMissionId));
          break;
        case TrainingControlsCommand.DAA:
          await store.triggerDaa(gcsId, getRequiredMissionId(selectedMissionId));
          break;
        case TrainingControlsCommand.REMOTE_ID_FAILURE:
          await store.triggerRemoteIdFailure(gcsId, vehicleId);
          break;
        case TrainingControlsCommand.RESTART_VEHICLE:
          await store.restartVehicle(gcsId, vehicleId);
          break;
      }

      setAction(AsyncAction.success());
      setSelectedCommand(null);
    } catch (error) {
      setAction(AsyncAction.failure(error));
      setSelectedCommand(null);
    }
  };

  const createActionHandler = (action: TrainingControlsCommand) => () => executeAction(action);

  const isVehicleSelected = !!selectedVehicleJointId;
  const isActionLoading = action.isLoading;

  const getButtonProps = (action: TrainingControlsCommand) => ({
    isEnabled: isVehicleSelected && !isActionLoading,
    isLoading: selectedCommand === action && isActionLoading,
    callback: createActionHandler(action)
  });

  return (
    <Dialog
      title={en.trainingControlsDialog.title}
      onClose={onClose}
      errorText={action.isFailure ? getLocalizedErrorMessage(action.error) : undefined}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: 'var(--dialog-common-width)'
        }}
      >
        <DropDownSearchList
          items={gcsVehicleIds}
          onItemSelected={setSelectedVehicleJointId}
          selectedItem={selectedVehicleJointId}
          matchesQuery={matchesVehicleQuery}
          getItemKey={(jointId) => jointId}
          renderResultRow={renderVehicleResultRow}
          renderSelectedItem={renderVehicleResultRow}
          placeholder={en.trainingControlsDialog.searchVehiclePlaceholder}
          noResultsText={en.trainingControlsDialog.noVehiclesFound}
        />

        <div style={{ display: 'flex', gap: '16px' }}>
          <SecondaryButton
            text={en.trainingControlsDialog.rtlButton}
            {...getButtonProps(TrainingControlsCommand.RTL)}
          />
          <SecondaryButton
            text={en.trainingControlsDialog.fcLossButton}
            {...getButtonProps(TrainingControlsCommand.FC_LOSS)}
          />
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <SecondaryButton
            text={en.trainingControlsDialog.safeUpButton}
            {...getButtonProps(TrainingControlsCommand.SAFE_UP)}
          />
          <SecondaryButton
            text={en.trainingControlsDialog.daaButton}
            {...getButtonProps(TrainingControlsCommand.DAA)}
          />
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <SecondaryButton
            text={en.trainingControlsDialog.landButton}
            {...getButtonProps(TrainingControlsCommand.LAND)}
          />
          <SecondaryButton
            text={en.trainingControlsDialog.ftsButton}
            {...getButtonProps(TrainingControlsCommand.FTS)}
          />
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <SecondaryButton
            text={en.trainingControlsDialog.remoteIdFailureButton}
            {...getButtonProps(TrainingControlsCommand.REMOTE_ID_FAILURE)}
          />
          <SecondaryButton
            text={en.trainingControlsDialog.restartVehicleButton}
            {...getButtonProps(TrainingControlsCommand.RESTART_VEHICLE)}
          />
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <SecondaryButton
            text={en.trainingControlsDialog.vehicleDisconnectionButton}
            {...getButtonProps(TrainingControlsCommand.VEHICLE_DISCONNECTION)}
          />
        </div>
      </div>
    </Dialog>
  );
}

export default TrainingControlsDialog;
